import { Component, Inject, Input, AfterViewInit, OnChanges, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { AlertService } from '@nano/common/alert';
import { CONFIG } from '@nano/core/config';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';

@Component({
  selector: 'app-send-message',
  templateUrl: './send-message.component.html',
  styleUrls: ['./send-message.component.less'],
  standalone: false
})
export class SendMessageComponent implements AfterViewInit, OnChanges {
  @Input() rowHeight: number;
  @Input() form: UntypedFormGroup;

  readonly messageBodyLength: number;

  private readonly config = inject(CONFIG);

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private alertService: AlertService
  ) {
    this.messageBodyLength = Math.min(this.config.messageBodyLength ?? 2000, 2000);
  }

  ngOnChanges(): void {
    this.form.valueChanges.subscribe(() => {
      this.checkFormErrors();
    });
  }

  checkFormErrors(): void {
    const subjectControl = this.form?.get('subject');
    const bodyControl = this.form?.get('body');

    const subjectErrors = subjectControl?.errors;
    const bodyErrors = bodyControl?.errors;

    if (subjectErrors || bodyErrors) {
      if (subjectErrors?.maxlength || bodyErrors?.maxlength) {
        this.alertService.alert({
          messageType: 'key',
          message: 'messages.threadDetails.exceedsMaxLength',
          messageParams: { maxlength: this.messageBodyLength },
          icon: 'reason-message',
          type: 'error'
        });
      }
    } else {
      this.alertService.cleanUp();
    }
  }

  ngAfterViewInit(): void {
    const bodyControl = this.form.get('body');
    const editableDiv = document.querySelector('.app-send-message-input');

    if (bodyControl && editableDiv) {
      const value = bodyControl.value as string;
      // eslint-disable-next-line @rx-angular/prefer-no-layout-sensitive-apis -- innerText keeps visible characters aligned with validation.
      (editableDiv as HTMLElement).innerText = value ?? '';
      // eslint-disable-next-line @rx-angular/prefer-no-layout-sensitive-apis -- innerText used to validate visible characters.
      this.updateBodyLengthError((editableDiv as HTMLElement).innerText);
    }
  }

  onInput(event: Event): void {
    const target = event.currentTarget as HTMLDivElement;
    if (this.form.get('body')) {
      // eslint-disable-next-line @rx-angular/prefer-no-layout-sensitive-apis -- innerText keeps visible characters aligned with validation.
      this.form.get('body').setValue(target.innerText);
      // eslint-disable-next-line @rx-angular/prefer-no-layout-sensitive-apis -- innerText used to validate visible characters.
      this.updateBodyLengthError(target.innerText);
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const target = event.currentTarget as HTMLDivElement;
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const pastedData = clipboardData.getData('text/html');
    if (pastedData) {
      const sanitizedData = this.sanitizeHtml(pastedData);
      document.execCommand('insertHTML', false, sanitizedData);
    } else {
      const textData = clipboardData.getData('text/plain');
      document.execCommand('insertText', false, textData);
    }

    if (this.form.get('body')) {
      // eslint-disable-next-line @rx-angular/prefer-no-layout-sensitive-apis -- innerText keeps visible characters aligned with validation.
      this.form.get('body').setValue(target.innerText);
      // eslint-disable-next-line @rx-angular/prefer-no-layout-sensitive-apis -- innerText used to validate visible characters.
      this.updateBodyLengthError(target.innerText);
    }
  }

  private sanitizeHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const elements = doc.body.querySelectorAll('*');

    elements.forEach((element) => {
      const style = element.getAttribute('style');
      if (style) {
        const allowedStyles = style
          .split(';')
          .filter((styleRule) => {
            const [property] = styleRule.split(':');
            return ['font-size', 'text-align', 'font-weight', 'font-style', 'text-decoration'].includes(property.trim());
          })
          .join(';');
        element.setAttribute('style', allowedStyles);
      }

      element.removeAttribute('class');
    });

    return doc.body.innerHTML;
  }

  private updateBodyLengthError(text: string): void {
    const bodyControl = this.form?.get('body');
    if (!bodyControl) return;

    const errors = { ...(bodyControl.errors ?? {}) };
    if (text.length > this.messageBodyLength) {
      errors.maxlength = { requiredLength: this.messageBodyLength, actualLength: text.length };
    } else {
      delete errors.maxlength;
    }

    bodyControl.setErrors(Object.keys(errors).length ? errors : null);
  }

  get showMaxLengthError(): boolean {
    return !!this.form?.get('body')?.hasError('maxlength');
  }
}
